import React from 'react';
import { motion } from 'framer-motion';

const AboutSection = ({ content = {} }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 border-y border-amber-500/20 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-0 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20"
          animate={{ x: [0, 50, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-0 w-96 h-96 bg-amber-500/5 rounded-full filter blur-3xl opacity-20"
          animate={{ x: [0, -50, 0] }}
          transition={{ duration: 12, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <motion.div
          className="grid md:grid-cols-2 gap-16 items-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Content */}
          <motion.div variants={itemVariants}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {content.about_title || 'About Ceylon Broker'}
            </h2>

            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              {content.about_description || 'Ceylon Broker is a trusted digital currency trading platform dedicated to making digital currency accessible to everyone. With transparent pricing and expert support, we\'ve helped thousands of customers enter the world of digital assets safely and securely. We are primarily engaged in financial transactions involving the creation, liquidation, or change in ownership of financial assets, and in facilitating financial transactions.'}
            </p>

            {/* Key points */}
            <div className="space-y-4 mb-8">
              {[
                { icon: '✓', text: 'Transparent pricing with no hidden fees' },
                { icon: '✓', text: '24/7 customer support' },
                { icon: '✓', text: 'Fast and secure transactions' },
                { icon: '✓', text: 'Dedicated support for every customer' },
              ].map((point, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="flex items-center gap-4 text-gray-300"
                >
                  <span className="text-amber-400 text-2xl font-bold">{point.icon}</span>
                  <span>{point.text}</span>
                </motion.div>
              ))}
            </div>

            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(251, 191, 36, 0.2)' }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all duration-300"
            >
              Learn More About Us
            </motion.button>
          </motion.div>

          {/* Stats Grid */}
          <motion.div
            className="grid grid-cols-2 gap-6"
            variants={containerVariants}
          >
            {[
              { number: content.years_in_business || 5, label: 'Years In Business', icon: '📅' },
              { number: `${content.total_users || 1000}+`, label: 'Happy Customers', icon: '👥' },
              { number: `${content.total_transactions || 5000}+`, label: 'Transactions', icon: '💱' },
              { number: content.trading_volume || '$10M+', label: 'Trading Volume', icon: '📈' },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                {/* Glass background */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-amber-500/5 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-all duration-500" />
                
                <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-amber-500/30 group-hover:border-amber-500/60 transition-all duration-300">
                  <div className="text-4xl mb-4">{stat.icon}</div>
                  <p className="text-3xl md:text-4xl font-bold text-transparent bg-gradient-to-r from-amber-400 to-amber-300 bg-clip-text mb-2">
                    {stat.number}
                  </p>
                  <p className="text-gray-400 text-sm">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
